import React, { useCallback, useEffect, useState } from 'react'
import { Breadcrumb, ConfigProvider, Flex, Spin, Tabs, TabsProps, Typography, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Route } from '../../routes/Route'
import { findRouteTrail, firstPagePath, menuLabel } from '../../layouts/AppShell/routeUtils'
import { usePageContainerContext, useRegisterPageContainer } from '../../layouts/AppShell/PageContainerContext'
import { PageContext, PageLayout, PageOptions } from './PageContext'
import { contentColumnStyle, fillColumnStyle } from './pageStyles'
import { formPageTheme } from './formPageTheme'

/** On narrow screens the window scrolls, so filled content needs a height of its own. */
const MOBILE_FILL_MIN_HEIGHT = 480

export interface PageProps {
  /** Defaults to the menu label of the current route. */
  title?: React.ReactNode
  /** One line below the title. */
  description?: React.ReactNode
  /** Header actions. Put the primary action last so it ends up rightmost. */
  actions?: React.ReactNode
  /** Selections that affect the data of the whole page, e.g. period or scenario. */
  context?: React.ReactNode
  /** Different views of the same page. Not for navigating to other pages. */
  tabs?: Pick<TabsProps, 'items' | 'activeKey' | 'onChange'>
  /**
   * `scroll` scrolls the content below the page header. `fill` stretches the content to the bottom of the
   * page instead, so a table can fill it and scroll its own rows.
   */
  layout?: PageLayout
  /** Shows the route trail above the header. Used in settings, where pages sit several levels deep. */
  breadcrumb?: boolean
  /** Padding around the content in pixels. Defaults to the theme's `paddingLG`. */
  contentPadding?: number
  loading?: boolean
  children?: React.ReactNode
}

/** Links a breadcrumb to the route's own page, or to the first page under a route that only groups pages. */
const breadcrumbPath = (route: Route) =>
  route.element || route.routes?.some(child => child.index) ? route.path : firstPagePath(route)

/**
 * Common page template for the content panel: breadcrumb, header, context row and tabs stay in place
 * while the content below them scrolls.
 */
const Page: React.FC<PageProps> = ({
  title,
  description: descriptionProp,
  actions,
  context,
  tabs,
  layout: layoutProp = 'scroll',
  breadcrumb = false,
  contentPadding: contentPaddingProp,
  loading: loadingProp,
  children
}) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { token } = theme.useToken()
  const pageContainer = usePageContainerContext()
  const windowScroll = pageContainer?.windowScroll ?? true
  const [actionsNode, setActionsNode] = useState<HTMLElement | null>(null)
  const [footerNode, setFooterNode] = useState<HTMLElement | null>(null)
  const [scrollNode, setScrollNode] = useState<HTMLElement | null>(null)
  const [options, setOptions] = useState<PageOptions | null>(null)
  const layout = options?.layout ?? layoutProp
  const description = options?.description ?? descriptionProp
  const contentWidth = options?.contentWidth
  const contentPadding = options?.contentPadding ?? contentPaddingProp ?? token.paddingLG
  const [loadingReporters, setLoadingReporters] = useState<ReadonlySet<symbol>>(new Set())
  const setReporterLoading = useCallback((reporter: symbol, reporterLoading: boolean) => {
    setLoadingReporters(reporters => {
      if (reporters.has(reporter) === reporterLoading) return reporters
      const next = new Set(reporters)
      if (reporterLoading) next.add(reporter)
      else next.delete(reporter)
      return next
    })
  }, [])
  const loading = !!loadingProp || loadingReporters.size > 0

  // The first load of a page shows a loading indicator; later loads dim the content already shown.
  // SettingLayout keeps one Page for all settings pages, so "first" is tracked per path.
  const [loadedPath, setLoadedPath] = useState<string | null>(null)
  useEffect(() => {
    if (!loading) setLoadedPath(pathname)
  }, [loading, pathname])
  const firstLoad = loading && loadedPath !== pathname
  const refreshing = loading && !firstLoad
  // Components reporting through usePageLoading must stay mounted; content under a loading prop can wait
  const renderContent = !firstLoad || loadingReporters.size > 0

  useRegisterPageContainer()

  const trail = findRouteTrail(pageContainer?.routes ?? [], pathname)
  const current = trail[trail.length - 1]
  const pageTitle = title ?? (current ? menuLabel(current, t) : t(`menu:${pathname}`, ''))

  const fill = layout === 'fill'
  // Filled content scrolls inside its own component (e.g. a grid), so only the scroll layout scrolls here
  const contentOverflow = fill ? 'hidden' : 'auto'
  const rowPadding = `0 ${token.paddingLG}px`
  const divider = `1px solid ${token.colorSplit}`
  // The content column and the form theme are separate options: a wide page can have a column without the theme
  const column = contentWidth ? <div style={contentColumnStyle(contentWidth)}>{children}</div> : children
  const content = options?.formTheme ? <ConfigProvider theme={formPageTheme}>{column}</ConfigProvider> : column

  return (
    <PageContext.Provider
      value={{
        actionsNode,
        footerNode,
        contentWidth,
        scrollContainer: windowScroll ? null : scrollNode,
        setOptions,
        setReporterLoading
      }}
    >
      <div style={fillColumnStyle}>
        <div style={{ flex: 'none' }}>
          {breadcrumb && trail.length > 1 && (
            <Breadcrumb
              style={{ padding: `${token.paddingXS}px ${token.paddingLG}px` }}
              items={trail.map(route => ({
                key: route.path,
                // The current page is not a link, but on detail pages its parent is
                title:
                  route.path === pathname ? (
                    menuLabel(route, t)
                  ) : (
                    <Link to={breadcrumbPath(route)}>{menuLabel(route, t)}</Link>
                  )
              }))}
            />
          )}
          {/* Tabs draw their own bottom line, so the header only needs one when there are no tabs right below it */}
          <div style={{ borderBottom: !tabs || context ? divider : undefined }}>
            <Flex
              justify="space-between"
              align="center"
              gap={token.marginSM}
              wrap
              style={{
                paddingBlock: token.padding,
                // On form pages the header uses the same centered column as the form below it
                paddingInline: contentWidth
                  ? `max(${token.paddingLG}px, calc((100% - ${contentWidth}px) / 2))`
                  : token.paddingLG
              }}
            >
              {/* The title gives way first; actions stay on one line and wrap below the title as a group */}
              <Flex vertical gap={token.marginXXS} style={{ flex: '1 1 auto', minWidth: 0 }}>
                <Flex align="center" gap={token.marginXS}>
                  {pageTitle && (
                    <Typography.Title level={3} ellipsis style={{ margin: 0 }}>
                      {pageTitle}
                    </Typography.Title>
                  )}
                </Flex>
                {description && (
                  <Typography.Text type="secondary" ellipsis>
                    {description}
                  </Typography.Text>
                )}
              </Flex>
              <Flex align="center" gap={token.marginXS} style={{ flex: 'none' }}>
                {actions}
                {/* PageActions of the page content render here */}
                <div ref={setActionsNode} style={{ display: 'contents' }} />
              </Flex>
            </Flex>
            {/* A block, not a flex row, so forms placed here get the full width to lay themselves out */}
            {context && <div style={{ padding: rowPadding, paddingBottom: token.paddingSM }}>{context}</div>}
          </div>
          {tabs && (
            <Tabs
              activeKey={tabs.activeKey}
              onChange={tabs.onChange}
              // Only the tab bar belongs to the header; the page renders the active tab's content itself
              items={tabs.items?.map(({ key, label, icon, disabled }) => ({ key, label, icon, disabled }))}
              tabBarStyle={{ margin: 0, paddingInline: token.paddingLG }}
            />
          )}
        </div>
        {/* The overlay sits outside the scrolling element, so it always covers the visible content */}
        <div style={{ ...fillColumnStyle, position: 'relative' }}>
          <div
            ref={setScrollNode}
            style={{
              flex: 1,
              minHeight: fill && windowScroll ? MOBILE_FILL_MIN_HEIGHT : 0,
              overflow: windowScroll ? undefined : contentOverflow,
              ...(fill && { display: 'flex', flexDirection: 'column' })
            }}
          >
            {firstLoad && (
              <Flex justify="center" style={{ paddingBlock: 100 }}>
                <Spin size="large" />
              </Flex>
            )}
            {/* Padding lives inside the scrolling element: sticky elements (e.g. table headers) stick to the
                scroll container's padding edge, which would leave content showing above them. */}
            {renderContent && (
              <div
                style={{
                  padding: contentPadding,
                  ...(fill && fillColumnStyle),
                  ...(firstLoad && { display: 'none' })
                }}
              >
                {content}
              </div>
            )}
          </div>
          {refreshing && (
            <Flex
              justify="center"
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                paddingTop: 100,
                background: `color-mix(in srgb, ${token.colorBgContainer} 60%, transparent)`
              }}
            >
              <Spin size="large" />
            </Flex>
          )}
        </div>
        {/* PageSaveBar of the page content renders here, below the scrolling content */}
        <div ref={setFooterNode} style={{ flex: 'none' }} />
      </div>
    </PageContext.Provider>
  )
}

export default Page
